import type { PiaItem } from '@/api/client';
import { cn } from '@/lib/utils';
import { CalendarDays, ShieldCheck } from 'lucide-react';

const statusColor: Record<string, string> = {
  Planned:   'badge-info',
  InReview:  'badge-warn',
  Completed: 'badge-good',
  Overdue:   'badge-bad',
};

export function PiaTracker({ items }: { items: PiaItem[] }) {
  return (
    <div className="card dashboard-panel p-2.5">
      <div className="dashboard-title mb-2 flex items-center gap-2">
        <ShieldCheck className="w-[18px] h-[18px] text-[#58a6ff]" /> Privacy Impact Assessment Tracker
      </div>

      <table className="w-full text-[11px]">
        <thead className="text-[#d7e4f1] bg-[#112a43] border-y border-[#1a4368]">
          <tr>
            <th className="text-left font-medium py-2 pl-3">Model/Feature</th>
            <th className="text-left font-medium py-2">PIA Status</th>
            <th className="text-left font-medium py-2">Risk Score</th>
            <th className="text-left font-medium py-2">Reviewer</th>
            <th className="text-left font-medium py-2 pr-3">Due Date</th>
            <th className="w-8" />
          </tr>
        </thead>
        <tbody>
          {items.map((p) => (
            <tr key={p.id} className="border-b border-[#15375b]/75 last:border-0 hover:bg-[#0d2740]">
              <td className="py-2 pl-3 font-medium text-[#e5edf7]">{p.modelOrFeatureName}</td>
              <td className="py-2">
                <span className={cn('inline-flex min-w-[70px] justify-center rounded-md px-2 py-1 text-[10px] font-semibold', statusColor[p.status] || 'badge-info')}>{displayStatus(p.status)}</span>
              </td>
              <td className="py-3">
                <RiskScore score={p.riskScore} />
              </td>
              <td className="py-2 text-[#c5d0df]"><Reviewer name={p.reviewer.reviewerName} /></td>
              <td className="py-2 text-[#c5d0df]"><DueDate value={p.dueDate} /></td>
              <td className="py-2 pr-3"><DueIcon score={p.riskScore} /></td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="pt-2 text-center text-[12px] text-[#58a6ff] hover:underline cursor-pointer">
        View All PIA Assessments &rarr;
      </div>
    </div>
  );
}

function RiskScore({ score }: { score: number }) {
  const color = score >= 61 ? '#EF4444' : score >= 31 ? '#F59E0B' : '#22C55E';
  const label = score >= 61 ? 'High' : score >= 31 ? 'Medium' : 'Low';
  return (
    <div className="flex items-center gap-2">
      <span className="min-w-[30px] rounded-md border px-1.5 py-0.5 text-center text-[11px] font-bold" style={{ color, borderColor: color, backgroundColor: `${color}22` }}>{score}</span>
      <span className="text-[11px] text-[#c5d0df]">{label}</span>
    </div>
  );
}

function Reviewer({ name }: { name: string }) {
  const initials = name.split(' ').map((part) => part[0]).join('').slice(0, 2).toUpperCase();
  return (
    <div className="flex items-center gap-2">
      <span className="grid h-5 w-5 place-items-center rounded-full bg-[#173d66] border border-[#2b5f91] text-[9px] font-bold text-[#a8d8ff]">{initials}</span>
      {name}
    </div>
  );
}

function DueDate({ value }: { value: string }) {
  return (
    <div className="flex items-center gap-2">
      <CalendarDays className="w-3.5 h-3.5 text-[#9fb0c4]" />
      {new Date(value).toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' })}
    </div>
  );
}

function DueIcon({ score }: { score: number }) {
  const isReady = score <= 35;
  return <span className={cn('grid h-4 w-4 place-items-center rounded-full border text-[9px]', isReady ? 'border-good text-good' : 'border-warn text-warn')}>{isReady ? '✓' : '!'}</span>;
}

function displayStatus(status: PiaItem['status']) {
  if (status === 'InReview') return 'In Review';
  return status;
}
